/* eslint-disable unicorn/prefer-module, react/no-deprecated, canonical/id-match, @typescript-eslint/naming-convention */
import React, { type ComponentType, startTransition } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { IntlProvider, type MessageFormatElement } from 'react-intl';
import { type IntlConfig } from 'react-intl/src/types';
import { type Options as SentryOptions } from '@sentry/types';

import { type TenantEnum } from '@jsmdg/tenancy';
import { fragmentContext } from './fragmentContext';
import { getFragmentName } from './getFragmentName';
import { logToSentryFactory } from './sentry';
import { SentryWrapper } from './SentryWrapper';

declare const __MFE_CONFIG__: {
    locales: string[];
    defaultLocale?: string;
    readClientFromServer?: boolean;
    bundleSharedDependencies?: boolean;
};

const { locales = [] } = typeof __MFE_CONFIG__ === 'undefined' ? {} : __MFE_CONFIG__;

// Create message map by loading all locale files from
// within the client source directory's messages folder.
// For en.json, de.json this will result in:
// {
//   en: { ... }
//   de: { ... }
// }
function importMessages(context: RequireContext) {
    return context
        .keys()
        .map((file: string) => {
            const [filename] = file.split(/[/\\]/u).pop()?.split('.') ?? [];

            return {
                code: filename,
                messages: context(file),
            };
        })
        .filter(({ code }: { code: string }) => locales.includes(code))
        .reduce(
            (
                accumulator: { [key: string]: MessageFormatElement },
                current: { code: string; messages: MessageFormatElement },
            ) => {
                accumulator[current.code] = current.messages;
                return accumulator;
            },
            {},
        );
}

function getMessages(): {
    messages: Record<string, MessageFormatElement>;
    localMessages: Record<string, MessageFormatElement>;
} {
    try {
        return {
            messages: importMessages(
                require.context(
                    `${process.env.APP_PATH}/src/client/messages`,
                    false,
                    /\.json$/u,
                    'sync',
                ),
            ),
            localMessages: importMessages(require.context('./messages', false, /\.json$/u, 'sync')),
        };
    } catch {
        return {
            messages: {},
            localMessages: {},
        };
    }
}

const { localMessages, messages } = getMessages();

function getMessagesForLocale(locale: string): IntlConfig['messages'] {
    const language = locale?.split('-')[0]?.split('_')[0] ?? '';

    return {
        ...messages[language],
        ...messages[locale],
        ...localMessages[language],
    } as unknown as IntlConfig['messages'];
}

export const logToSentry = logToSentryFactory(getFragmentName());

export interface FragmentProperties<PropertiesType> {
    readonly baseUrl: string;
    readonly tenant: TenantEnum;
    readonly locale: string;
    readonly sentry?: SentryOptions;
    readonly rootComponentProps: PropertiesType & JSX.IntrinsicAttributes;
}

export type InitDependenciesFn = (
    parameters: Pick<FragmentProperties<null>, 'baseUrl' | 'tenant' | 'locale'>,
) => Promise<void>;

export function createFragment<PropertiesType = {}, ContextType = {}>(
    RootComponent: ComponentType<PropertiesType>,
    getFragmentContext = (
        _properties: Omit<FragmentProperties<PropertiesType>, 'sentry'>,
    ): ContextType => ({}) as ContextType,
    initDependencies: InitDependenciesFn = async () => {},
) {
    const Fragment = ({
        baseUrl,
        locale,
        rootComponentProps,
        sentry = { enabled: false },
        tenant,
    }: FragmentProperties<PropertiesType>) => {
        return (
            <IntlProvider
                locale={locale ?? 'de'}
                defaultLocale="de"
                messages={getMessagesForLocale(locale ?? 'de')}
            >
                <SentryWrapper sentry={sentry}>
                    <fragmentContext.Provider
                        value={getFragmentContext({
                            baseUrl,
                            tenant,
                            locale,
                            rootComponentProps,
                        })}
                    >
                        <RootComponent {...rootComponentProps} />
                    </fragmentContext.Provider>
                </SentryWrapper>
            </IntlProvider>
        );
    };

    async function init(rootElement: HTMLElement) {
        const fragmentData = window?.Evelin?.data?.[
            getFragmentName()
        ] as FragmentProperties<PropertiesType>;
        if (!fragmentData) {
            throw new Error(
                `Could not initialize ${getFragmentName()} because there was no fragment data for it inside Evelin.data. Got: ${fragmentData}`,
            );
        }

        if (!rootElement) {
            throw new Error(
                `Could not initialize ${getFragmentName()} because no valid container element was given. Got: ${rootElement}`,
            );
        }

        const { baseUrl, locale, rootComponentProps, sentry, tenant } = fragmentData;

        await initDependencies({ baseUrl, tenant, locale });

        const fragment = (
            <Fragment
                baseUrl={baseUrl}
                tenant={tenant}
                locale={locale}
                sentry={sentry}
                rootComponentProps={rootComponentProps}
            />
        );

        const hydrateFunction = (): void => {
            hydrateRoot(rootElement, fragment, {
                identifierPrefix: `${getFragmentName()}-`,
            });
        };

        startTransition(hydrateFunction);
    }

    async function renderFunction(
        rootElement: Element | DocumentFragment,
        {
            baseUrl = '/',
            locale,
            rootComponentProps,
            sentry,
            tenant,
        }: FragmentProperties<PropertiesType>,
    ) {
        if (!rootElement) return;

        await initDependencies({ baseUrl, tenant, locale });

        const fragment = (
            <Fragment
                baseUrl={baseUrl}
                tenant={tenant}
                locale={locale}
                rootComponentProps={rootComponentProps}
                sentry={sentry}
            />
        );

        createRoot(rootElement, {
            identifierPrefix: `${getFragmentName()}-`,
        }).render(fragment);
    }

    return { Fragment, init, render: renderFunction };
}
